// frontend/shared-ui/src/forms/useShipmentContext.js
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'fiberhome.shipmentContext';

const DEFAULT = {
    label: '',
    totalCbm: 0,
    totalShipmentCost: 0,   // ← NEW
    costPerCbm: 0,          // ← kept for legacy, auto-derived when not manually set
    rent: 0,
    customs: 0,
    insurance: 0,
    misc: 0,
    targetMarginPct: 30,
};

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT;
        return { ...DEFAULT, ...JSON.parse(raw) };
    } catch {
        return DEFAULT;
    }
}

export function useShipmentContext() {
    const [context, setContext] = useState(loadFromStorage);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
        } catch { }
    }, [context]);

    const setField = useCallback((key, value) => {
        setContext((c) => ({ ...c, [key]: value }));
    }, []);

    const reset = useCallback(() => setContext(DEFAULT), []);

    // ── Derived values ─────────────────────────────────────
    const totalCbm = Number(context.totalCbm) || 0;
    const totalShipmentCost = Number(context.totalShipmentCost) || 0;

    // Freight cost per CBM: prefer derived from shipment total, fall back to manual costPerCbm
    const freightPerCbm = totalCbm > 0
        ? totalShipmentCost / totalCbm
        : (Number(context.costPerCbm) || 0);

    const overheadTotal =
        Number(context.rent || 0) +
        Number(context.customs || 0) +
        Number(context.insurance || 0) +
        Number(context.misc || 0);

    const overheadPerCbm =
        totalCbm > 0 ? overheadTotal / totalCbm : 0;

    const fullPerCbm = freightPerCbm + overheadPerCbm;

    return {
        context,
        setField,
        reset,
        freightPerCbm,
        overheadTotal,
        overheadPerCbm,
        fullPerCbm,
        totalCbm,
        totalShipmentCost,
    };
}